import React, { useState, useRef } from 'react';
import { createDonor, fetchDonors, fetchLists, fetchNotes, fetchGroups, createGroup } from '../lib/supabaseDb';
import { authFetch, authPost } from '../lib/authFetch';
import './SettingsView.css';

function SettingsView({ dataMode, onClose, user, subscription, onSignOut }) {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const fileInputRef = useRef(null);

  // Calculate days remaining in trial
  const getTrialDaysRemaining = () => {
    if (subscription?.plan !== 'trial' || !subscription?.trial_ends_at) return null;
    const now = new Date();
    const trialEnd = new Date(subscription.trial_ends_at);
    const diff = trialEnd - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Handle manage subscription click
  const handleManageSubscription = async () => {
    console.log('handleManageSubscription called');
    console.log('subscription:', subscription);
    setManagingSubscription(true);
    try {
      console.log('Making request to /api/create-portal-session...');
      const response = await authPost('/api/create-portal-session', {});
      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);
      if (data.url) {
        console.log('Redirecting to:', data.url);
        window.location.href = data.url;
      } else if (data.error) {
        console.error('Portal session error:', data.error);
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Failed to open subscription portal:', error);
      alert('Failed to open subscription portal: ' + error.message);
    }
    setManagingSubscription(false);
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError(null);

    try {
      const response = await authPost('/api/delete-account', {});
      const data = await response.json();

      if (response.ok && data.success) {
        // Account deleted - sign out
        onSignOut();
      } else {
        setDeleteError(data.error || 'Failed to delete account');
        setDeleting(false);
      }
    } catch (error) {
      console.error('Delete account error:', error);
      setDeleteError('Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  const trialDaysRemaining = getTrialDaysRemaining();

  const handleExport = async (format = 'json') => {
    setExporting(true);
    try {
      // In real mode with user, fetch directly from Supabase
      if (dataMode === 'real' && user?.id) {
        const donors = await fetchDonors(user.id);
        const lists = await fetchLists(user.id);
        const notes = await fetchNotes(user.id);

        if (format === 'csv') {
          // Create CSV content for donors
          const headers = ['Name', 'Stage', 'Amount', 'Last Contact', 'Sentiment', 'Narrative'];
          const csvRows = [headers.join(',')];

          donors.forEach(donor => {
            const row = [
              `"${(donor.name || '').replace(/"/g, '""')}"`,
              `"${(donor.stage || '').replace(/"/g, '""')}"`,
              `"${(donor.amount || '').replace(/"/g, '""')}"`,
              `"${(donor.last_contact || '').replace(/"/g, '""')}"`,
              `"${(donor.sentiment || '').replace(/"/g, '""')}"`,
              `"${(donor.narrative || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`
            ];
            csvRows.push(row.join(','));
          });

          const csvContent = csvRows.join('\n');
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `penelope-donors-${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          // JSON export
          const exportData = { donors, lists, notes, exportedAt: new Date().toISOString() };
          const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `penelope-export-${new Date().toISOString().split('T')[0]}.json`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      } else {
        // Fallback to API if no user
        const response = await authFetch(`/api/export?mode=${dataMode}`);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `penelope-export-${dataMode}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
    setExporting(false);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    setUploadStatus(null);

    // In real mode with logged-in user, parse file and save directly to Supabase
    if (dataMode === 'real' && user?.id) {
      try {
        const text = await file.text();
        let donors = [];

        // Try to parse as JSON first
        try {
          const json = JSON.parse(text);
          if (Array.isArray(json)) {
            donors = json;
          } else if (json.donors) {
            donors = Object.entries(json.donors).map(([name, data]) => ({ name, ...data }));
          }
        } catch {
          // Try CSV parsing
          const lines = text.split('\n').filter(l => l.trim());
          if (lines.length > 1) {
            const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
            donors = lines.slice(1).map(line => {
              const values = line.split(',');
              const donor = {};
              headers.forEach((h, i) => {
                donor[h] = values[i]?.trim() || '';
              });
              return donor;
            });
          }
        }

        // Get or create an "Imported" group for imported donors
        let importGroupId = null;
        try {
          const existingGroups = await fetchGroups(user.id);
          const importGroup = existingGroups.find(g => g.name === 'Imported');
          if (importGroup) {
            importGroupId = importGroup.id;
          } else {
            // Create the "Imported" group
            const newGroup = await createGroup(user.id, 'Imported', '#6b7280');
            if (newGroup) {
              importGroupId = newGroup.id;
            }
          }
        } catch (groupErr) {
          console.error('Error getting/creating import group:', groupErr);
        }

        let addedCount = 0;
        for (const donor of donors) {
          const result = await createDonor(user.id, {
            name: donor.name || donor.Name || 'Unknown',
            narrative: donor.narrative || donor.notes || '',
            stage: donor.stage || 'prospecting',
            lastContact: donor.lastContact || donor.last_contact || null,
            amount: donor.amount || 'Unknown',
            sentiment: donor.sentiment || null,
            groupId: importGroupId
          });
          if (result) addedCount++;
        }

        setUploadStatus({
          success: true,
          message: `Added ${addedCount} people to Supabase from ${file.name}`
        });
        setUploading(false);
        return;
      } catch (error) {
        console.error('Supabase import failed:', error);
        setUploadStatus({
          success: false,
          message: 'Failed to import to Supabase: ' + error.message
        });
        setUploading(false);
        return;
      }
    }

    // Otherwise use the existing API
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', dataMode);

    try {
      const response = await authFetch('/api/import-donors', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setUploadStatus({
          success: true,
          message: `Added ${result.donorsAdded} people from ${file.name}`
        });
      } else {
        setUploadStatus({
          success: false,
          message: result.error || 'Failed to process file'
        });
      }
    } catch (error) {
      setUploadStatus({
        success: false,
        message: 'Failed to upload file. Is the server running?'
      });
    }

    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-panel" onClick={e => e.stopPropagation()}>
        <button className="settings-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <h2 className="settings-title">Settings</h2>

        {/* Plan Info Section */}
        {subscription && (
          <div className="settings-section plan-section">
            <label className="settings-label">Your Plan</label>
            <div className="plan-info">
              <div className={`plan-badge plan-badge-${subscription.plan}`}>
                {subscription.plan === 'trial' ? 'Trial' : subscription.plan === 'pro' ? 'Pro' : 'Free'}
              </div>
              {subscription.plan === 'trial' && trialDaysRemaining !== null && (
                <p className="plan-status">
                  {trialDaysRemaining > 0
                    ? `${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''} left in trial`
                    : 'Trial expired'}
                </p>
              )}
              {subscription.plan === 'pro' && subscription.current_period_end && (
                <p className={`plan-status ${subscription.cancel_at_period_end ? 'canceling' : ''}`}>
                  {subscription.cancel_at_period_end
                    ? `Your plan ends on ${formatDate(subscription.current_period_end)}`
                    : `Next billing: ${formatDate(subscription.current_period_end)}`}
                </p>
              )}
              {subscription.plan === 'free' && (
                <p className="plan-status">
                  {subscription.donor_limit || 20} people limit
                </p>
              )}
            </div>
            {subscription.plan === 'pro' && subscription.stripe_customer_id && (
              <button
                className="manage-subscription-btn"
                onClick={handleManageSubscription}
                disabled={managingSubscription}
              >
                {managingSubscription ? 'Loading...' : 'Manage Subscription'}
              </button>
            )}
          </div>
        )}

        <div className="settings-section">
          <label className="settings-label">Import People</label>
          <div
            className={`upload-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,.txt,.json"
              onChange={(e) => handleFileUpload(e.target.files[0])}
              style={{ display: 'none' }}
            />
            {uploading ? (
              <>
                <div className="upload-spinner" />
                <span className="upload-text">Processing with AI...</span>
              </>
            ) : (
              <>
                <svg className="upload-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span className="upload-text">Drop a file or click to upload</span>
                <span className="upload-hint">CSV, Excel, JSON, or text files</span>
              </>
            )}
          </div>
          {uploadStatus && (
            <div className={`upload-status ${uploadStatus.success ? 'success' : 'error'}`}>
              {uploadStatus.success ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              )}
              {uploadStatus.message}
            </div>
          )}
          <p className="settings-hint">
            AI will parse your file and extract names, amounts, contact info, and notes.
          </p>
        </div>

        <div className="settings-section">
          <label className="settings-label">Export Data</label>
            <div className="export-buttons">
              <button
                className={`export-button ${exporting ? 'exporting' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={exporting}
              >
                {exporting ? (
                  <>
                    <div className="export-spinner" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Export as CSV
                  </>
                )}
              </button>
              <button
                className={`export-button export-button-secondary ${exporting ? 'exporting' : ''}`}
                onClick={() => handleExport('json')}
                disabled={exporting}
              >
                {exporting ? (
                  <>
                    <div className="export-spinner" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Export as JSON
                  </>
                )}
              </button>
            </div>
            <p className="settings-hint">
              CSV exports people data (opens in Excel/Sheets). JSON includes people, lists, and notes.
            </p>
        </div>

        {/* Logout section */}
        {onSignOut && (
          <div className="settings-section settings-logout-section">
            <button className="logout-button" onClick={onSignOut}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign Out
            </button>
          </div>
        )}

        {/* Cancel Account / Danger Zone */}
        {onSignOut && (
          <div className="settings-section danger-section">
            <label className="settings-label danger-label">Danger Zone</label>

            {!showDeleteConfirm ? (
              <button
                className="wipe-button"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
                Cancel Account
              </button>
            ) : (
              <div className="wipe-confirm">
                <p className="wipe-warning">
                  This will permanently delete your account and all your data including:
                  <br />• All people/donors
                  <br />• All lists and groups
                  <br />• All notes and chat history
                  <br />• Your subscription (if any)
                  <br /><br />
                  <strong>This action cannot be undone.</strong>
                </p>
                {deleteError && (
                  <p className="wipe-warning" style={{ marginTop: '0.5rem' }}>
                    Error: {deleteError}
                  </p>
                )}
                <div className="wipe-buttons">
                  <button
                    className="wipe-cancel"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteError(null);
                    }}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="wipe-confirm-btn"
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Delete My Account'}
                  </button>
                </div>
              </div>
            )}

            <p className="settings-hint" style={{ color: '#dc2626' }}>
              Permanently delete your account and all associated data.
            </p>
          </div>
        )}

        <div className="settings-legal">
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <span>|</span>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          <span className="settings-copyright">© {new Date().getFullYear()} Knowroom, Inc.</span>
        </div>
      </div>
    </div>
  );
}

export default SettingsView;
